import { mensagemDeErro } from '@common/errors/mensagemDeErro';
import {
  Alert,
  AlertDialog,
  Breadcrumb,
  Button,
  PageHeader,
  Spinner,
  useToast,
} from '@ntconsult/aluma-react';
import { parseAsString, parseAsStringLiteral, useQueryStates } from 'nuqs';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  useCampanha,
  useConvites,
  useDesativarCampanha,
  useMarcarTextoRevisado,
  useModelosConvite,
  useProgresso,
  useSincronizar,
} from '../hooks';
import type { Campanha } from '../types/Campanha';
import type { FiltroLote } from '../types/Painel';
import type { Progresso } from '../types/Progresso';
import { AbrirCampanhaDialog } from './AbrirCampanhaDialog';
import { BarraDaEtapa } from './BarraDaEtapa';
import { CampanhaFormModal } from './CampanhaFormModal';
import { ChecklistPreparacao, usePeriodo } from './ChecklistPreparacao';
import { EnviarConviteModal } from './EnviarConviteModal';
import { ExportarModal } from './ExportarModal';
import { FaseBadge } from './FaseBadge';
import { buscaDoFunil } from './funil';
import { LembrarDialog } from './LembrarDialog';
import { LinkCampanhaSecao } from './LinkCampanhaSecao';
import { type ModelosEmailHandle, ModelosEmailSecao } from './ModelosEmailSecao';
import { PainelAcompanhamento } from './PainelAcompanhamento';
import { ParticipantesAba } from './ParticipantesAba';
import { ACAO_NA_URL, type Acao, emPreparacao, regrasDaFase } from './regrasDaFase';
import { useRotuloDaAcao } from './rotuloDaAcao';
import { SincronizacaoStatus } from './SincronizacaoStatus';

/** A única vista além da página da fase: o texto do convite (spec 36 R9). */
const SECOES = ['convite'] as const;

/** Envio pelo cabeçalho: os que não receberam (linha 9) ou o diálogo "Lembrar" (spec 31 R1). */
type Envio = 'naoRecebidos' | 'lembrar';
const FILTRO_NAO_RECEBIDOS: Partial<FiltroLote> = {
  situacao: 'elegivel',
  convite: ['nao_enviado'],
};

export function CampanhaDetalhePage() {
  const { id = '' } = useParams();
  const { t } = useTranslation(['campanhas', 'common', 'erros']);
  const campanha = useCampanha(id);
  const progresso = useProgresso(id);
  const modelos = useModelosConvite(id);
  // Mantém o polling da rodada em qualquer vista: a ação principal depende dela (R24).
  useConvites(id);

  if (campanha.isLoading || progresso.isLoading || modelos.isLoading)
    return <Spinner label={t('common:loading')} />;
  if (campanha.isError || !campanha.data)
    return <Alert variant="danger" title={t('erros:CAMPANHA_NAO_ENCONTRADA')} />;
  if (progresso.isError || !progresso.data)
    return (
      <div className="flex flex-col gap-8">
        <PageHeader title={campanha.data.nome} />
        <Alert variant="danger" title={t(`erros:${mensagemDeErro(progresso.error)}`)} />
      </div>
    );
  return <Detalhe campanha={campanha.data} progresso={progresso.data} />;
}

function Detalhe({ campanha, progresso }: { campanha: Campanha; progresso: Progresso }) {
  const { t } = useTranslation(['campanhas', 'participantes', 'convites', 'common', 'erros']);
  const { toast } = useToast();
  // Pelo `nuqs`, como os filtros da lista: um `Navigate` do react-router com a URL que ele
  // conhece apagaria os parâmetros que o `nuqs` gravou sem passar por ele.
  const [url, setUrl] = useQueryStates(
    { secao: parseAsStringLiteral(SECOES), acao: parseAsString },
    { history: 'push' },
  );
  const navigate = useNavigate();
  const sincronizacao = useSincronizar(campanha.id);
  const desativar = useDesativarCampanha(campanha.id);
  const marcarTextoRevisado = useMarcarTextoRevisado(campanha.id);
  const rotuloDaAcao = useRotuloDaAcao();
  const rotulo = (a: Acao) => rotuloDaAcao(a, progresso);
  const [editando, setEditando] = useState(false);
  const [exportando, setExportando] = useState(false);
  const [abrindo, setAbrindo] = useState(false);
  const [confirmandoSinc, setConfirmandoSinc] = useState<Acao | null>(null);
  const [sincronizandoCom, setSincronizandoCom] = useState<Acao | null>(null);
  const [pausando, setPausando] = useState<'pausar' | 'fechar' | null>(null);
  const [envio, setEnvio] = useState<Envio | null>(null);
  const editor = useRef<ModelosEmailHandle>(null);

  const regras = regrasDaFase(progresso);
  const fase = progresso.fase;
  const preparacao = emPreparacao(fase);

  async function confirmarPausa() {
    try {
      await desativar.mutateAsync();
    } catch (error) {
      toast.danger({ title: t(`erros:${mensagemDeErro(error)}`) });
    }
    setPausando(null);
  }

  /** R8 da 28: abrir sincroniza só se nunca houve sincronização e não há lista nenhuma. */
  function aposAbrir() {
    if (progresso.sincronizadaEm === null && progresso.naCampanha + progresso.excluidos === 0)
      void sincronizacao.sincronizar();
  }

  /** "Concluir revisão" (spec 36 R10): salva os rascunhos, grava o revisado e volta; com falha, fica. */
  async function concluirRevisao() {
    if (editor.current?.temRascunho() && !(await editor.current.salvarRascunhos())) return;
    try {
      await marcarTextoRevisado.mutateAsync();
    } catch (error) {
      toast.danger({ title: t(`erros:${mensagemDeErro(error)}`) });
      return;
    }
    void setUrl({ secao: null });
  }

  const executar: Record<Acao, () => void> = {
    carregarAxiis: () => setConfirmandoSinc('carregarAxiis'),
    carregarDeNovo: () => setConfirmandoSinc('carregarDeNovo'),
    revisarTexto: () => void setUrl({ secao: 'convite' }),
    editarTexto: () => void setUrl({ secao: 'convite' }),
    abrirCampanha: () => setAbrindo(true),
    reabrir: () => setAbrindo(true),
    enviarConvites: () => setEnvio('naoRecebidos'),
    enviarNaoRecebidos: () => setEnvio('naoRecebidos'),
    lembrar: () => setEnvio('lembrar'),
    baixarPlanilha: () => setExportando(true),
    pausar: () => setPausando('pausar'),
    fechar: () => setPausando('fechar'),
    editar: () => setEditando(true),
    editarDatas: () => setEditando(true),
  };

  // `?acao=` vinda do Início (spec 29 R22, spec 36 R13): só dispara se ainda for o primário
  // daqui; se a fase mudou no caminho, é ignorada em silêncio. O efeito tira a `acao` da URL,
  // que é um sistema externo, e dispara a ação pedida por ela.
  const pedida = url.acao;
  const disparar =
    pedida !== null && regras.primario && ACAO_NA_URL[regras.primario] === pedida
      ? regras.primario
      : null;
  // biome-ignore lint/correctness/useExhaustiveDependencies: `executar` muda a cada render; a ação é `disparar`
  useEffect(() => {
    if (pedida === null) return;
    void setUrl({ acao: null }, { history: 'replace' });
    if (disparar) executar[disparar]();
  }, [pedida, disparar, setUrl]);

  const botao = (a: Acao, texto?: string) => {
    const primario = regras.primario === a;
    return (
      <Button
        key={a}
        variant={primario ? 'primary' : 'secondary'}
        data-acao-principal={primario || undefined}
        data-acao={a}
        loading={sincronizandoCom === a && sincronizacao.isPending}
        onClick={executar[a]}
      >
        {texto ?? rotulo(a)}
      </Button>
    );
  };

  const periodo = usePeriodo(campanha);

  return (
    <div className="flex flex-col gap-8">
      {url.secao === 'convite' ? (
        <>
          {/* Links do router: o `useBlocker` do editor pergunta antes de sair com rascunho (R11). */}
          <PageHeader
            breadcrumb={
              <Breadcrumb>
                <Breadcrumb.List>
                  <Breadcrumb.Item>
                    <Breadcrumb.Link asChild>
                      <Link to={`/campanhas/${campanha.id}`}>{campanha.nome}</Link>
                    </Breadcrumb.Link>
                  </Breadcrumb.Item>
                  <Breadcrumb.Separator />
                  <Breadcrumb.Item>
                    <Breadcrumb.Page>{t('campanhas:convite.titulo')}</Breadcrumb.Page>
                  </Breadcrumb.Item>
                </Breadcrumb.List>
              </Breadcrumb>
            }
            title={t('campanhas:convite.titulo')}
            badge={<FaseBadge fase={fase} />}
          />
          <ModelosEmailSecao
            ref={editor}
            campanhaId={campanha.id}
            link={
              (fase === 'agendada' || fase === 'aberta') && <LinkCampanhaSecao id={campanha.id} />
            }
          />
          {preparacao && (
            <div>
              <Button
                variant="primary"
                data-acao-principal
                data-acao="concluirRevisao"
                loading={marcarTextoRevisado.isPending}
                onClick={() => void concluirRevisao()}
              >
                {t('campanhas:convite.concluirRevisao')}
              </Button>
            </div>
          )}
        </>
      ) : (
        <>
          <PageHeader
            title={campanha.nome}
            badge={<FaseBadge fase={fase} />}
            description={!preparacao && periodo}
            actions={
              regras.secundarias.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {regras.secundarias.map((a) => botao(a))}
                </div>
              )
            }
          />

          {preparacao ? (
            <>
              <ChecklistPreparacao campanha={campanha} progresso={progresso} botao={botao} />
              {progresso.naCampanha + progresso.excluidos >= 1 ? (
                <section className="flex flex-col gap-4" data-lista>
                  <h2 className="text-lg font-semibold text-foreground">
                    {t('campanhas:participantes')}
                  </h2>
                  <SincronizacaoStatus campanhaId={campanha.id} naCampanha={progresso.naCampanha} />
                  <ParticipantesAba campanhaId={campanha.id} fase={fase} />
                </section>
              ) : (
                // Só a falha da última sincronização, sem lista (R5).
                progresso.ultimaSincronizacaoFalhou && (
                  <SincronizacaoStatus campanhaId={campanha.id} naCampanha={0} />
                )
              )}
            </>
          ) : (
            <div className="flex flex-col gap-5">
              <BarraDaEtapa titulo={t('campanhas:acompanhamento')}>
                {regras.primario && botao(regras.primario)}
              </BarraDaEtapa>
              <PainelAcompanhamento campanha={campanha} progresso={progresso} />
            </div>
          )}
        </>
      )}

      <CampanhaFormModal open={editando} onClose={() => setEditando(false)} campanha={campanha} />

      <ExportarModal
        open={exportando}
        onClose={() => setExportando(false)}
        campanhaId={campanha.id}
      />

      <AbrirCampanhaDialog
        campanha={campanha}
        progresso={progresso}
        open={abrindo}
        onClose={() => setAbrindo(false)}
        onAberta={aposAbrir}
      />

      <EnviarConviteModal
        campanhaId={campanha.id}
        open={envio === 'naoRecebidos'}
        publico={progresso.naoConvidados}
        selecao={{ filtro: FILTRO_NAO_RECEBIDOS, totalEsperado: progresso.naoConvidados }}
        // Quem, dos que não receberam, não tem e-mail (spec 31 R19).
        onVerSemEmail={() =>
          navigate({ search: buscaDoFunil({ convite: 'nao_enviado', temEmail: 'nao' }) })
        }
        onFechar={() => setEnvio(null)}
      />

      <LembrarDialog
        campanhaId={campanha.id}
        // Todos responderam com ele aberto: fecha (spec 31 R8).
        open={envio === 'lembrar' && progresso.naoResponderam > 0}
        onFechar={() => setEnvio(null)}
      />

      <AlertDialog
        open={confirmandoSinc !== null}
        onOpenChange={(o) => !o && setConfirmandoSinc(null)}
      >
        <AlertDialog.Content>
          <AlertDialog.Title>{confirmandoSinc && rotulo(confirmandoSinc)}</AlertDialog.Title>
          <AlertDialog.Description>{t('participantes:confirmar')}</AlertDialog.Description>
          <AlertDialog.Footer>
            <AlertDialog.Cancel variant="secondary">{t('common:cancel')}</AlertDialog.Cancel>
            <AlertDialog.Action
              variant="primary"
              onClick={async () => {
                // Fecha na hora; o botão fica em `loading` até terminar (28 R20).
                setSincronizandoCom(confirmandoSinc);
                setConfirmandoSinc(null);
                await sincronizacao.sincronizar();
                setSincronizandoCom(null);
              }}
            >
              {confirmandoSinc && rotulo(confirmandoSinc)}
            </AlertDialog.Action>
          </AlertDialog.Footer>
        </AlertDialog.Content>
      </AlertDialog>

      <AlertDialog open={pausando !== null} onOpenChange={(o) => !o && setPausando(null)}>
        <AlertDialog.Content>
          <AlertDialog.Title>
            {pausando === 'fechar' ? rotulo('fechar') : rotulo('pausar')}
          </AlertDialog.Title>
          <AlertDialog.Description>
            {t(
              pausando === 'fechar'
                ? 'campanhas:confirmar.fechar'
                : 'campanhas:confirmar.desativar',
              {
                nome: campanha.nome,
              },
            )}
          </AlertDialog.Description>
          <AlertDialog.Footer>
            <AlertDialog.Cancel variant="secondary">{t('common:cancel')}</AlertDialog.Cancel>
            <AlertDialog.Action
              variant="primary"
              loading={desativar.isPending}
              onClick={confirmarPausa}
            >
              {pausando === 'fechar' ? rotulo('fechar') : rotulo('pausar')}
            </AlertDialog.Action>
          </AlertDialog.Footer>
        </AlertDialog.Content>
      </AlertDialog>
    </div>
  );
}
